/**
 * Thin wrappers over the Capacitor plugins we use.
 *
 * Every one of these must be a no-op (or a sensible web fallback) when the app
 * runs in a browser, so `npm run dev` and the CI build never need a device.
 */
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/** The web ballot's stand-in: Android browsers honour this; iOS Safari has no vibration API at all. */
const webVibrate = (pattern: number | number[]) => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
  } catch {
    /* a nicety */
  }
};

export async function haptic(style: 'light' | 'medium' | 'heavy' = 'light') {
  if (!isNative) {
    webVibrate({ light: 8, medium: 15, heavy: 25 }[style]);
    return;
  }
  const map = {
    light: ImpactStyle.Light,
    medium: ImpactStyle.Medium,
    heavy: ImpactStyle.Heavy,
  } as const;
  try {
    await Haptics.impact({ style: map[style] });
  } catch {
    // Haptics are a nicety; a device without a taptic engine must not throw.
  }
}

/** The "done!" pattern (a double tap on iOS) — for a ballot going in. */
export async function hapticSuccess() {
  if (!isNative) {
    webVibrate([12, 60, 18]);
    return;
  }
  try {
    await Haptics.notification({ type: NotificationType.Success });
  } catch {
    // Same as above: a nicety, never an error.
  }
}

/**
 * How a share attempt ended.
 *
 * `shared` — the sheet completed (iOS reports a cancelled sheet as an error,
 *            Android reports the chosen app; both are as reliable as the
 *            platform allows, no more).
 * `copied` — no share sheet exists here, so the link went to the clipboard.
 *            A copy is not a share, and anything that rewards sharing must
 *            not treat it as one.
 * `dismissed` — the user backed out, or nothing worked.
 */
export type ShareOutcome = 'shared' | 'copied' | 'dismissed';

export async function share(options: {
  title: string;
  text?: string;
  url?: string;
}): Promise<ShareOutcome> {
  if (isNative) {
    try {
      await Share.share({ dialogTitle: options.title, ...options });
      return 'shared';
    } catch {
      return 'dismissed'; // User dismissed the sheet.
    }
  }
  if (navigator.share) {
    try {
      await navigator.share(options);
      return 'shared';
    } catch {
      return 'dismissed';
    }
  }
  try {
    await navigator.clipboard.writeText(options.url ?? options.text ?? options.title);
    return 'copied';
  } catch {
    return 'dismissed';
  }
}

/**
 * Open an external URL.
 *
 * On device this uses the in-app browser (SFSafariViewController / Custom Tabs)
 * rather than kicking the user out to Safari or Chrome — Apple expects web
 * content reached from an app to come back, and a full app switch loses state.
 */
export async function openExternal(url: string) {
  if (isNative) {
    await Browser.open({ url, presentationStyle: 'popover', toolbarColor: '#08080A' });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/* ---------------------------------------------------------------- storage */

/**
 * Capacitor Preferences on device, localStorage on web. Values are JSON.
 * Reads never throw: a corrupt or absent value falls back to `fallback`.
 */
export async function readStored<T>(key: string, fallback: T): Promise<T> {
  try {
    const raw = isNative
      ? (await Preferences.get({ key })).value
      : window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export async function writeStored(key: string, value: unknown): Promise<void> {
  const raw = JSON.stringify(value);
  try {
    if (isNative) await Preferences.set({ key, value: raw });
    else window.localStorage.setItem(key, raw);
  } catch {
    // Storage full or blocked (private mode). State stays in memory this session.
  }
}
